import { ChevronDown, Search, SlidersHorizontal } from 'lucide-react'
import type { FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { ASSIGNED, BOARD_STATUS_OPTIONS, NOT_ASSIGNED, type BoardFilters, type BoardStatusFilter } from '@/lib/board/boardFilters'
import {
  BOARD_ELEMENTS,
  MAX_BOARD_COLUMNS,
  MIN_BOARD_COLUMNS,
  type BoardElement,
  type BoardPrefs,
} from '@/lib/board/boardPrefs'
import { PROCEDURE_TYPE_COLOR } from '@/lib/procedureTypeColors'
import { PROCEDURE_TYPE_OPTIONS, PROCEDURE_TYPE_LABELS, type ProcedureAssignee, type ProcedureType } from '@/types/cgt'

const ANY = 'all'

/** One compact height and text size, so every filter fits on one line on a desktop screen. */
const CONTROL = 'h-9 text-sm'

/**
 * The board's filters. Changes are a draft until Apply (or Enter in the search
 * box) applies them, so several can be set without a reload per control.
 */
export function BoardFilterBar({
  draft,
  onDraftChange,
  patients,
  users,
  showAssignee,
  customers,
  dirty,
  onGo,
  onReset,
}: {
  draft: BoardFilters
  onDraftChange: (next: BoardFilters) => void
  /** [mrn, label] pairs. */
  patients: [string, string][]
  users: ProcedureAssignee[]
  showAssignee: boolean
  /** Customer options ({ value: id, label }); the filter shows only when there are some. */
  customers: { value: string; label: string }[]
  dirty: boolean
  onGo: () => void
  onReset: () => void
}) {
  const set = <K extends keyof BoardFilters>(key: K, value: BoardFilters[K]) => onDraftChange({ ...draft, [key]: value })

  function submit(e: FormEvent) {
    e.preventDefault()
    onGo()
  }

  function toggleType(type: ProcedureType, on: boolean) {
    const next = on ? [...draft.types, type] : draft.types.filter(t => t !== type)
    set('types', PROCEDURE_TYPE_OPTIONS.filter(t => next.includes(t)))
  }

  const typeLabel =
    draft.types.length === 0
      ? 'All types'
      : draft.types.length === 1
        ? PROCEDURE_TYPE_LABELS[draft.types[0]]
        : `${draft.types.length} types`

  return (
    <form onSubmit={submit} className='mb-5 flex flex-wrap items-center gap-1.5 xl:flex-nowrap'>
      <div className='relative min-w-[11rem] flex-1'>
        <Search className='absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
        <Input
          className={cn(CONTROL, 'pl-8')}
          placeholder='Search by patient, MRN, or procedure…'
          value={draft.q}
          onChange={e => set('q', e.target.value)}
          aria-label='Search procedures'
        />
      </div>

      <Popover>
        <PopoverTrigger asChild>
          <Button type='button' variant='outline' className={cn(CONTROL, 'w-32 shrink-0 justify-between px-2.5 font-normal')} aria-label='Filter by type'>
            {typeLabel}
            <ChevronDown className='h-4 w-4 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent align='start' className='w-56 p-2'>
          {PROCEDURE_TYPE_OPTIONS.map(type => (
            <label key={type} className='flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted'>
              <Checkbox checked={draft.types.includes(type)} onCheckedChange={on => toggleType(type, on === true)} />
              <span className='h-2.5 w-2.5 rounded-full' style={{ backgroundColor: PROCEDURE_TYPE_COLOR[type] }} />
              {PROCEDURE_TYPE_LABELS[type]}
            </label>
          ))}
        </PopoverContent>
      </Popover>

      <Select value={draft.patientMrn || ANY} onValueChange={v => set('patientMrn', v === ANY ? '' : v)}>
        <SelectTrigger className={cn(CONTROL, 'w-40 shrink-0')} aria-label='Filter by patient'>
          <SelectValue placeholder='All patients' />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>All patients</SelectItem>
          {patients.map(([mrn, label]) => (
            <SelectItem key={mrn} value={mrn}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={draft.status} onValueChange={v => set('status', v as BoardStatusFilter)}>
        <SelectTrigger className={cn(CONTROL, 'w-32 shrink-0')} aria-label='Filter by status'>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {BOARD_STATUS_OPTIONS.map(o => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Assigned / Not assigned for everyone (z8nrz7f9u5): "Not assigned" is the
          supervisor's list to hand out. Named people need the roster, which
          only someone who can assign reads. */}
      <Select value={draft.assigneeId || ANY} onValueChange={v => set('assigneeId', v === ANY ? '' : v)}>
        <SelectTrigger className={cn(CONTROL, 'w-36 shrink-0')} aria-label='Filter by assignee'>
          <SelectValue placeholder='Any assignee' />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Any assignee</SelectItem>
          <SelectItem value={ASSIGNED}>Assigned</SelectItem>
          <SelectItem value={NOT_ASSIGNED}>Not assigned</SelectItem>
          {showAssignee &&
            users.map(u => (
              <SelectItem key={u.id} value={u.id}>
                {u.name}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>

      {/* Whose runs (z8nrz7e71k). Only Kit and MES runs carry a customer, so
          choosing one shows only those — runs without one are never folded in. */}
      {customers.length > 0 && (
        <Select value={draft.customerId || ANY} onValueChange={v => set('customerId', v === ANY ? '' : v)}>
          <SelectTrigger className={cn(CONTROL, 'w-40 shrink-0')} aria-label='Filter by customer'>
            <SelectValue placeholder='All customers' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All customers</SelectItem>
            {customers.map(c => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <div className='flex shrink-0 items-center gap-1'>
        <label className='text-xs text-muted-foreground' htmlFor='board-created-from'>
          Raised
        </label>
        <Input
          id='board-created-from'
          type='date'
          value={draft.createdFrom}
          max={draft.createdTo || undefined}
          onChange={e => set('createdFrom', e.target.value)}
          className={cn(CONTROL, 'w-[8.5rem] px-2')}
          aria-label='Raised on or after'
        />
        <span className='text-xs text-muted-foreground'>to</span>
        <Input
          type='date'
          value={draft.createdTo}
          min={draft.createdFrom || undefined}
          onChange={e => set('createdTo', e.target.value)}
          className={cn(CONTROL, 'w-[8.5rem] px-2')}
          aria-label='Raised on or before'
        />
      </div>

      <Button type='button' variant='outline' size='sm' className='h-9 shrink-0' onClick={onReset}>
        Reset
      </Button>
      <Button type='submit' size='sm' className='h-9 shrink-0 px-4' variant={dirty ? 'default' : 'secondary'}>
        Apply
      </Button>
    </form>
  )
}

/** Cards per row and which card elements show, saved per user. */
export function BoardCardSettings({
  prefs,
  onColumns,
  onShown,
  onReset,
}: {
  prefs: BoardPrefs
  onColumns: (columns: number) => void
  onShown: (key: BoardElement, shown: boolean) => void
  onReset: () => void
}) {
  const options = Array.from({ length: MAX_BOARD_COLUMNS - MIN_BOARD_COLUMNS + 1 }, (_, i) => MIN_BOARD_COLUMNS + i)
  return (
    <div className='flex items-center gap-2'>
      <label className='text-sm text-muted-foreground' htmlFor='board-columns'>
        Columns
      </label>
      <Select value={String(prefs.columns)} onValueChange={v => onColumns(Number(v))}>
        <SelectTrigger id='board-columns' className='w-16' aria-label='Cards per row'>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(n => (
            <SelectItem key={n} value={String(n)}>
              {n}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant='outline' size='sm' className='gap-1.5'>
            <SlidersHorizontal className='h-4 w-4' />
            Customize cards
          </Button>
        </PopoverTrigger>
        <PopoverContent align='end' className='w-64'>
          <p className='mb-2 text-sm font-medium'>Show on cards</p>
          <div className='space-y-2'>
            {BOARD_ELEMENTS.map(({ key, label }) => (
              <label key={key} className='flex cursor-pointer items-center justify-between gap-3 text-sm'>
                {label}
                <Switch checked={prefs.show[key]} onCheckedChange={on => onShown(key, on)} aria-label={`Show ${label}`} />
              </label>
            ))}
          </div>
          <Button variant='ghost' size='sm' className='mt-3 w-full' onClick={onReset}>
            Restore defaults
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  )
}
